import { css, styled, useMediaQuery } from '@mui/material';
import MUIStep from '@mui/material/Step';
import MUIStepLabel from '@mui/material/StepLabel';
import MUIStepper from '@mui/material/Stepper';
import MUITypography from '@mui/material/Typography';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { device } from 'src/constants/mediaQueries';
import { createStepIconComponent } from './CustomIcon';

const Stepper = styled(MUIStepper, {
  shouldForwardProp: (prop: string) => prop !== 'fullWidth' && prop !== 'margin' && prop !== 'lighterIcons',
})<{ fullWidth: boolean; margin: string; lighterIcons: boolean }>`
  ${({ theme, orientation, fullWidth, margin, lighterIcons }) => css`
    ${orientation === 'vertical' &&
    /* istanbul ignore next */
    css`
      .MuiStepConnector-root {
        margin-left: 16px;
      }
    `};
    ${!lighterIcons &&
    css`
      .MuiStepConnector-line {
        border-color: ${theme.palette.grey[900]};
      }
    `};
    ${fullWidth && /* istanbul ignore next */ 'width: 100%;'}
    ${margin && `margin: ${margin};`}
  `};
  @media only screen and ${device.smallMobile} {
    .MuiStepLabel-iconContainer {
      padding-right: 0;
    }
  }
`;

const Label = styled(MUITypography, {
  shouldForwardProp: (prop: string) => prop !== 'active' && prop !== 'done' && prop !== 'contrastText',
})<{
  active: boolean;
  done: boolean;
  contrastText: boolean;
}>`
  color: ${({ theme, active, done, contrastText }) =>
    (active && contrastText ? /* istanbul ignore next */ theme.palette.brand.contrastText : theme.palette.grey[900]) ||
    /* istanbul ignore next */ (done && contrastText ? theme.palette.brand.contrastText : theme.palette.grey[900]) ||
    /* istanbul ignore next */ (!active && contrastText ? theme.palette.brand.contrastText : theme.palette.grey[600])};
  font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
`;

interface CheckoutStepperProps {
  currentStep: number;
  orientation: 'vertical' | 'horizontal';
  translationKey: string;
  fullWidth?: boolean;
  contrastText?: boolean;
  largeText?: boolean;
  lighterIcons?: boolean;
  margin: string;
  label1Key: string;
  label2Key: string;
  label3Key: string;
  connector?: ReactElement<any, any>;
  blackAndWhiteActiveIcon?: boolean;
}

export function StepperComponent({
  currentStep,
  orientation,
  translationKey,
  fullWidth = false,
  contrastText = false,
  largeText = false,
  lighterIcons = false,
  margin,
  label1Key,
  label2Key,
  label3Key,
  connector,
  blackAndWhiteActiveIcon,
}: Readonly<CheckoutStepperProps>) {
  const { t } = useTranslation(translationKey);
  const isMobile = useMediaQuery(device.smallMobile);
  const StepIconComponent = createStepIconComponent(largeText, lighterIcons, blackAndWhiteActiveIcon);
  Label.defaultProps = { variant: largeText ? /* istanbul ignore next */ 'body1' : 'body2' };

  return (
    <Stepper
      activeStep={currentStep}
      orientation={orientation}
      fullWidth={fullWidth}
      margin={margin}
      lighterIcons={lighterIcons}
      connector={connector}
    >
      <MUIStep>
        <MUIStepLabel StepIconComponent={StepIconComponent}>
          {!isMobile && (
            <Label active={currentStep === 0} done={currentStep > 0} contrastText={contrastText}>
              {t(label1Key)}
            </Label>
          )}
        </MUIStepLabel>
      </MUIStep>
      <MUIStep>
        <MUIStepLabel StepIconComponent={StepIconComponent}>
          {!isMobile && (
            <Label active={currentStep === 1} done={currentStep > 1} contrastText={contrastText}>
              {t(label2Key)}
            </Label>
          )}
        </MUIStepLabel>
      </MUIStep>
      <MUIStep>
        <MUIStepLabel StepIconComponent={StepIconComponent}>
          {!isMobile && (
            <Label active={currentStep === 2} done={currentStep > 2} contrastText={contrastText}>
              {t(label3Key)}
            </Label>
          )}
        </MUIStepLabel>
      </MUIStep>
    </Stepper>
  );
}
